// Australian-friendly formatters for currency and date.

export function formatAUD(
  amount: number,
  opts: { compact?: boolean; minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  if (opts.compact && Math.abs(amount) >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(2)}m`;
  }
  if (opts.compact && Math.abs(amount) >= 1_000) {
    return `$${(amount / 1_000).toFixed(1)}k`;
  }
  const max = opts.maximumFractionDigits ?? 0;
  const min = Math.min(opts.minimumFractionDigits ?? 0, max);
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(amount);
}

/**
 * Security price formatter — never round a unit price to whole dollars. Cents
 * stocks need more precision than blue chips, so the decimal count scales with
 * size: < $1 → 4dp, < $1000 → 2dp, otherwise 2dp with no rounding surprises.
 */
export function formatPriceAUD(price: number): string {
  const abs = Math.abs(price);
  const dp = abs > 0 && abs < 1 ? 4 : 2;
  return formatAUD(price, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

/** Unit/share count — up to 4dp for fractional holdings, grouped. */
export function formatUnits(units: number): string {
  return units.toLocaleString('en-AU', { maximumFractionDigits: 4 });
}

/** Whole days since an ISO date (negative if in the future), or null if unparseable. */
export function daysOld(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

/** A valuation/price date is "stale" once it is more than `maxDays` old (default 3). */
export function isStale(iso: string | null | undefined, maxDays = 3): boolean {
  const d = daysOld(iso);
  return d == null || d > maxDays;
}

export function formatPct(value: number, fractionDigits = 1): string {
  return `${(value * 100).toFixed(fractionDigits)}%`;
}

/** True for a bare calendar date `YYYY-MM-DD` (no time component). */
export function isDateOnly(s: string | null | undefined): boolean {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

// dd/mm/yyyy — Australian date convention. Date-only strings are formatted from
// their parts (NOT via `new Date()`, which parses them as UTC midnight and can
// shift a day in a non-UTC timezone) so review/DOB/FY dates never display a day out.
export function formatDateAU(iso: string | null | undefined): string {
  if (!iso) return '—';
  if (isDateOnly(iso)) {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Today as `YYYY-MM-DD` in a given IANA timezone (default Australian eastern). */
export function todayDateOnly(timeZone = 'Australia/Sydney'): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

/** Chronological compare of two `YYYY-MM-DD` strings: -1 / 0 / 1. */
export function compareDateOnly(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Whole calendar days between `iso` (date-only) and today (date-only). Positive = in the past. */
export function daysOldDateOnly(iso: string | null | undefined, today = todayDateOnly()): number | null {
  if (!isDateOnly(iso)) return null;
  const a = Date.parse(`${iso}T00:00:00Z`);
  const b = Date.parse(`${today}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

export function formatRelativeDays(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

export interface AgeResult {
  years: number;
  months: number;
  days: number;
  display: string;
}

export function calculateAge(dob: string | null | undefined): AgeResult | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  if (Number.isNaN(birth.getTime())) return null;

  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  let days = today.getDate() - birth.getDate();

  if (days < 0) {
    months -= 1;
    const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += lastMonth.getDate();
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return {
    years,
    months,
    days,
    display: `${years}y ${months}m ${days}d`,
  };
}

/** Current Australian financial-year ending year (FY to 30 June). */
export function currentAuFy(today = todayDateOnly()): number {
  const [y, m] = today.split('-').map(Number);
  return m >= 7 ? y + 1 : y; // Jul-Dec -> next year's FY end; Jan-Jun -> this year
}

/** Label like "FY2025-26" for a FY ending year. */
export function fyLabel(fy: number): string {
  return `FY${fy - 1}-${String(fy).slice(2)}`;
}
